import {
  type CaptureOptions,
  type EventName,
  type Properties,
} from 'posthog-js';

import { __DEV__ } from '../constants';
import { getSessionId } from '../session';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
if (!POSTHOG_KEY) {
  throw new Error('Missing env var: NEXT_PUBLIC_POSTHOG_KEY');
}

type WithoutSessionId<T> = T & {
  sessionId?: never;
};
type PropertiesWithoutSessionId = WithoutSessionId<Properties>;

/**
 * The posthog library needs to load before we are able to use any of its methods.
 * To avoid requiring `isLoaded` checks everywhere we try to track throughout the codebase
 * (and also to prevent missed tracking calls), we use a buffer and flush system here that
 * waits until the library is fully loaded before invoking any of its methods.
 */

let isLoaded = false;

let identifyBuffer: Array<
  Parameters<typeof import('posthog-js').posthog.identify>
> = [];
const flushIdentify = () => {
  if (isLoaded) {
    for (const args of identifyBuffer) {
      posthog?.identify(...args);
    }
    identifyBuffer = [];
  }
};

const identify: (
  ...args: Parameters<typeof import('posthog-js').posthog.identify>
) => void = (
  new_distinct_id,
  userPropertiesToSet = {},
  userPropertiesToSetOnce = {},
) => {
  identifyBuffer.push([
    new_distinct_id,
    { ...userPropertiesToSet, sessionId: getSessionId() },
    userPropertiesToSetOnce,
  ]);
};

let captureBuffer: Array<
  Parameters<typeof import('posthog-js').posthog.capture>
> = [];
const flushCapture = () => {
  if (isLoaded) {
    for (const args of captureBuffer) {
      posthog?.capture(...args);
    }
    captureBuffer = [];
  }
};
const capture: (
  event_name: EventName,
  properties?: PropertiesWithoutSessionId | null,
  options?: CaptureOptions,
) => void = (event_name, properties = {}, options) => {
  captureBuffer.push([
    event_name,
    { ...properties, sessionId: getSessionId() },
    options,
  ]);
  flushCapture();
};

const flushAll = () => {
  flushIdentify();
  flushCapture();
};

let posthog: Nullable<typeof import('posthog-js').default> = null;

const phog = {
  init: () => {
    if (posthog) return posthog;
    return (async () => {
      posthog = (await import('posthog-js')).default;
      posthog.init(POSTHOG_KEY, {
        api_host: '/ingest',
        ui_host: 'https://us.posthog.com',
        debug: __DEV__,
        loaded: (posthogInstance) => {
          isLoaded = true;
          flushAll();
          if (__DEV__) {
            posthogInstance.debug();
          }
        },
      });
      return posthog;
    })();
  },
  identify,
  capture,
};

export default phog;
